import { useFetcher } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useCallback, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import { Button, Input, TextArea, TextS, Hero } from '~/tenset-components'
import { Namespace } from '~/i18n'
import type { ContactAction } from '~/routes/$locale.contact'

function ContactForm(_props: unknown) {
  const { t } = useTranslation([Namespace.CONTACT_PAGE, Namespace.COMMON])

  const fetcher = useFetcher<ContactAction>()

  const ref = useRef<HTMLFormElement | null>(null)
  const isSubmitted = fetcher.data?.ok
  const isLoading = fetcher.state === 'submitting'

  const [recaptchaToken, setRecaptchaToken] = useState('')
  const { executeRecaptcha } = useGoogleReCaptcha()

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return
    }

    const token = await executeRecaptcha('formSubmit')
    setRecaptchaToken(token)
  }, [executeRecaptcha])

  useEffect(() => {
    const isDone = fetcher.state === 'idle' && fetcher.data !== null

    if (ref.current && isDone && fetcher.data?.ok) {
      ref.current.reset()
    }

    handleReCaptchaVerify()
  }, [fetcher, handleReCaptchaVerify])

  return (
    <fetcher.Form
      ref={ref}
      className="flex w-full flex-col gap-4 lg:gap-8"
      method="post"
    >
      <div className="flex flex-col gap-4 md:flex-row lg:gap-6">
        <Input
          label={t('common:form.name')}
          placeholder={'John'}
          name="name"
          errorMessage={fetcher.data?.validations?.name}
          disabled={isSubmitted}
        />

        <Input
          label={t('common:form.lastName')}
          placeholder={'Doe'}
          name="lastName"
          errorMessage={fetcher.data?.validations?.lastName}
          disabled={isSubmitted}
        />
      </div>

      <Input
        label={t('common:form.workEmail')}
        placeholder="john@gmail.com"
        name="workEmail"
        errorMessage={fetcher.data?.validations?.workEmail}
        disabled={isSubmitted}
      />

      <TextArea
        label={t('common:form.message')}
        placeholder={t('common:form.messagePlaceholder')}
        name="message"
        errorMessage={fetcher.data?.validations?.message}
        disabled={isSubmitted}
      />

      <input
        name="recaptcha"
        type="hidden"
        value={recaptchaToken}
        onChange={handleReCaptchaVerify}
      />

      <div className="mt-2 flex flex-col items-center md:mt-0 md:flex-none md:items-start">
        <Button
          type="submit"
          className="w-full md:w-auto"
          disabled={isLoading || isSubmitted}
          loading={isLoading}
        >
          <TextS isBold>
            {isSubmitted ? t('common:form.submitted') : t('common:form.submit')}
          </TextS>
        </Button>

        <div className="mt-2">
          <TextS className="font-sora text-neutral-100">
            {t('common:form.answerMessage')}
          </TextS>
        </div>

        <div className="mt-2">
          <TextS className="font-sora text-neutral-100">
            {t('recaptcha.part-1')}{' '}
            <a href="https://policies.google.com/privacy" className="underline">
              {t('recaptcha.part-2')}
            </a>{' '}
            {t('recaptcha.part-3')}{' '}
            <a href="https://policies.google.com/terms" className="underline">
              {t('recaptcha.part-4')}
            </a>{' '}
            {t('recaptcha.part-5')}
          </TextS>
        </div>
      </div>
    </fetcher.Form>
  )
}

export function HeroContact(_props: unknown) {
  const { t } = useTranslation([Namespace.CONTACT_PAGE])

  return (
    <section className="grid place-items-center">
      <Hero
        title={t('hero.title')}
        description={t('hero.description')}
        rightContent={<ContactForm />}
      />
    </section>
  )
}
